import { audioCtx } from "./context.js";
import { globalSfxVolume } from "./sfx.js";

/** Synthesize a retro arcade gun shot sound dynamically using oscillators and noise filter envelopes */
export function playSynthPew(volume: number, rate = 1.0) {
  if (audioCtx.state === "suspended") audioCtx.resume();

  const now = audioCtx.currentTime;
  const duration = 0.15 / rate;

  // 1. Oscillator for the frequency-swept core punch
  const osc = audioCtx.createOscillator();
  osc.type = "sawtooth";
  osc.frequency.setValueAtTime(850 * rate, now);
  osc.frequency.exponentialRampToValueAtTime(60 * rate, now + duration);

  // 2. White noise for the explosive crunch
  const bufferSize = Math.max(100, Math.floor(audioCtx.sampleRate * duration));
  const buffer = audioCtx.createBuffer(1, bufferSize, audioCtx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < bufferSize; i++) {
    data[i] = Math.random() * 2 - 1;
  }
  const noise = audioCtx.createBufferSource();
  noise.buffer = buffer;

  // 3. Bandpass filter to sculpt the noise explosion
  const filter = audioCtx.createBiquadFilter();
  filter.type = "bandpass";
  filter.frequency.setValueAtTime(1000 * rate, now);
  filter.frequency.exponentialRampToValueAtTime(120 * rate, now + duration);
  filter.Q.value = 1.5;

  // 4. Envelopes for volume decay
  const oscGain = audioCtx.createGain();
  oscGain.gain.setValueAtTime(0.4, now);
  oscGain.gain.exponentialRampToValueAtTime(0.001, now + duration);

  const noiseGain = audioCtx.createGain();
  noiseGain.gain.setValueAtTime(0.6, now);
  noiseGain.gain.exponentialRampToValueAtTime(0.001, now + (0.1 / rate));

  const mainGain = audioCtx.createGain();
  mainGain.gain.value = volume * globalSfxVolume;

  // Connections
  osc.connect(oscGain);
  oscGain.connect(mainGain);

  noise.connect(filter);
  filter.connect(noiseGain);
  noiseGain.connect(mainGain);

  mainGain.connect(audioCtx.destination);

  osc.start(now);
  osc.stop(now + duration);
  noise.start(now);
  noise.stop(now + duration);
}

/* ---- 8-Bit NES Synthesizers for Battle City mode ---- */
export function playSynthPew8Bit(volume: number, rate = 1.0) {
  if (audioCtx.state === "suspended") audioCtx.resume();
  const now = audioCtx.currentTime;
  const duration = 0.1 / rate;
  const osc = audioCtx.createOscillator();
  osc.type = "square"; // NES Square pulse wave
  osc.frequency.setValueAtTime(600 * rate, now);
  osc.frequency.linearRampToValueAtTime(100 * rate, now + duration);
  const gain = audioCtx.createGain();
  gain.gain.setValueAtTime(0.25 * volume * globalSfxVolume, now);
  gain.gain.exponentialRampToValueAtTime(0.001, now + duration);
  osc.connect(gain);
  gain.connect(audioCtx.destination);
  osc.start(now);
  osc.stop(now + duration);
}

export function playSynthExplosion8Bit(volume: number) {
  if (audioCtx.state === "suspended") audioCtx.resume();
  const now = audioCtx.currentTime;
  const duration = 0.35;
  const bufferSize = audioCtx.sampleRate * duration;
  const buffer = audioCtx.createBuffer(1, bufferSize, audioCtx.sampleRate);
  const data = buffer.getChannelData(0);
  let lastVal = 0;
  // NES noise emulation: downsampled random steps
  for (let i = 0; i < bufferSize; i++) {
    if (i % 8 === 0) lastVal = Math.random() * 2 - 1;
    data[i] = lastVal;
  }
  const noise = audioCtx.createBufferSource();
  noise.buffer = buffer;
  const filter = audioCtx.createBiquadFilter();
  filter.type = "lowpass";
  filter.frequency.setValueAtTime(300, now);
  filter.frequency.exponentialRampToValueAtTime(30, now + duration);
  const gain = audioCtx.createGain();
  gain.gain.setValueAtTime(0.5 * volume * globalSfxVolume, now);
  gain.gain.exponentialRampToValueAtTime(0.001, now + duration);
  noise.connect(filter);
  filter.connect(gain);
  gain.connect(audioCtx.destination);
  noise.start(now);
  noise.stop(now + duration);
}

export function playSynthPowerup8Bit(volume: number) {
  if (audioCtx.state === "suspended") audioCtx.resume();
  const now = audioCtx.currentTime;
  const osc = audioCtx.createOscillator();
  osc.type = "square";
  const arpeggio = [261.63, 329.63, 392.00, 523.25, 659.25, 783.99, 1046.50]; // C4, E4, G4, C5, E5, G5, C6
  const stepTime = 0.05;
  arpeggio.forEach((freq, idx) => {
    osc.frequency.setValueAtTime(freq, now + idx * stepTime);
  });
  const gain = audioCtx.createGain();
  gain.gain.setValueAtTime(0.15 * volume * globalSfxVolume, now);
  gain.gain.setValueAtTime(0.15 * volume * globalSfxVolume, now + (arpeggio.length - 1) * stepTime);
  gain.gain.exponentialRampToValueAtTime(0.001, now + arpeggio.length * stepTime);
  osc.connect(gain);
  gain.connect(audioCtx.destination);
  osc.start(now);
  osc.stop(now + arpeggio.length * stepTime);
}

export function playSynthOof8Bit(volume: number) {
  if (audioCtx.state === "suspended") audioCtx.resume();
  const now = audioCtx.currentTime;
  const duration = 0.08;
  const osc = audioCtx.createOscillator();
  osc.type = "triangle";
  osc.frequency.setValueAtTime(150, now);
  osc.frequency.linearRampToValueAtTime(50, now + duration);
  const gain = audioCtx.createGain();
  gain.gain.setValueAtTime(0.3 * volume * globalSfxVolume, now);
  gain.gain.exponentialRampToValueAtTime(0.001, now + duration);
  osc.connect(gain);
  gain.connect(audioCtx.destination);
  osc.start(now);
  osc.stop(now + duration);
}

export function playSynthReloading8Bit(volume: number) {
  if (audioCtx.state === "suspended") audioCtx.resume();
  const now = audioCtx.currentTime;
  const osc = audioCtx.createOscillator();
  osc.type = "square";
  osc.frequency.setValueAtTime(800, now);
  osc.frequency.setValueAtTime(1200, now + 0.06);
  const gain = audioCtx.createGain();
  gain.gain.setValueAtTime(0.12 * volume * globalSfxVolume, now);
  gain.gain.exponentialRampToValueAtTime(0.001, now + 0.12);
  osc.connect(gain);
  gain.connect(audioCtx.destination);
  osc.start(now);
  osc.stop(now + 0.12);
}

export function playSynthWin8Bit(volume: number) {
  if (audioCtx.state === "suspended") audioCtx.resume();
  const now = audioCtx.currentTime;
  const notes = [523.25, 523.25, 523.25, 523.25, 659.25, 587.33, 659.25, 783.99, 1046.50]; // C5 C5 C5 C5 E5 D5 E5 G5 C6
  const durations = [0.1, 0.1, 0.1, 0.2, 0.2, 0.1, 0.1, 0.1, 0.6];
  let time = now;
  notes.forEach((freq, idx) => {
    const osc = audioCtx.createOscillator();
    osc.type = "square";
    osc.frequency.setValueAtTime(freq, time);
    const gain = audioCtx.createGain();
    gain.gain.setValueAtTime(0.18 * volume * globalSfxVolume, time);
    gain.gain.exponentialRampToValueAtTime(0.001, time + durations[idx] - 0.02);
    osc.connect(gain);
    gain.connect(audioCtx.destination);
    osc.start(time);
    osc.stop(time + durations[idx]);
    time += durations[idx];
  });
}

export function playSynthLose8Bit(volume: number) {
  if (audioCtx.state === "suspended") audioCtx.resume();
  const now = audioCtx.currentTime;
  const notes = [392.00, 370.00, 349.23, 311.13, 293.66, 261.63]; // G4 F#4 F4 D#4 D4 C4
  const durations = [0.15, 0.15, 0.15, 0.2, 0.2, 0.8];
  let time = now;
  notes.forEach((freq, idx) => {
    const osc = audioCtx.createOscillator();
    osc.type = "triangle";
    osc.frequency.setValueAtTime(freq, time);
    const gain = audioCtx.createGain();
    gain.gain.setValueAtTime(0.3 * volume * globalSfxVolume, time);
    gain.gain.exponentialRampToValueAtTime(0.001, time + durations[idx] - 0.02);
    osc.connect(gain);
    gain.connect(audioCtx.destination);
    osc.start(time);
    osc.stop(time + durations[idx]);
    time += durations[idx];
  });
}
